<template>
  <!-- Laid out to the ເພີ່ມຮ້ານຄ້າ design: the dialog supplies the title and the
       ×; this component owns a scrolling body with the action pinned beneath it,
       so the save button never scrolls out of reach on a short window. -->
  <div class="cod-shop-form">
    <div class="form-scroll">
      <div class="fld">
        <label class="fld-label">{{ $t("codAdvance.shop_name") }} <i class="req">*</i></label>
        <input class="fld-input" type="text" v-model="form.shop_name" :placeholder="$t('codAdvance.shop_name_placeholder')">
      </div>

      <!-- The customer is chosen once and never re-pointed: an enrollment carries debt. -->
      <div class="fld" v-if="!isEdit">
        <label class="fld-label">{{ $t("codAdvance.enroll_customer") }} <i class="req">*</i></label>
        <p class="fld-hint">{{ $t("codAdvance.customer_search_hint") }}</p>
        <div class="search-wrap">
          <i class="fal fa-search search-icon"></i>
          <input class="fld-input has-icon" type="text" v-model="customerQuery" @input="onSearch"
                 :placeholder="$t('codAdvance.customer_search_placeholder')">
        </div>

        <div class="picker">
          <div class="pane">
            <div class="pane-head">
              <span>{{ $t("codAdvance.search_results") }}</span>
              <span class="pane-count" v-if="customerResults.length">{{ $t("codAdvance.items_count", { n: customerResults.length }) }}</span>
            </div>
            <div class="pane-body">
              <p class="pane-empty" v-if="searching">{{ $t("codAdvance.searching") }}</p>
              <p class="pane-empty" v-else-if="!customerResults.length">
                {{ customerQuery.length >= 2 ? $t("codAdvance.customer_not_found") : $t("codAdvance.type_min_chars") }}
              </p>
              <div v-else>
                <div class="person-row" v-for="row in customerResults" :key="row.id"
                     :class="{'is-picked': isPicked(row), 'is-taken': !!row.enrolled_shop_id}"
                     @click="pickCustomer(row)">
                  <span class="avatar">{{ initialOf(row) }}</span>
                  <span class="person-text">
                    <b class="person-name">{{ fullName(row) }}</b>
                    <span class="person-tel"><i class="fal fa-phone"></i> {{ row.tel }}</span>
                  </span>
                  <span class="taken-tag" v-if="row.enrolled_shop_id">{{ $t("codAdvance.already_enrolled_tag") }}</span>
                  <i class="fas fa-check-circle picked-tick" v-else-if="isPicked(row)"></i>
                </div>
              </div>
            </div>
          </div>

          <div class="pane">
            <div class="pane-head is-selected-head">
              <span><i class="fal fa-user"></i> {{ $t("codAdvance.selected_customer") }}</span>
            </div>
            <div class="pane-body is-centered">
              <p class="pane-empty" v-if="!selectedCustomer">{{ $t("codAdvance.no_customer_selected") }}</p>
              <div class="chosen" v-else>
                <span class="avatar is-lg">{{ initialOf(selectedCustomer) }}</span>
                <span class="person-text">
                  <b class="person-name">{{ fullName(selectedCustomer) }}</b>
                  <span class="person-tel"><i class="fal fa-phone"></i> {{ selectedCustomer.tel }}</span>
                </span>
                <button class="chosen-x" type="button" @click="clearCustomer" :aria-label="$t('codAdvance.remove')">
                  <i class="fal fa-times"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="fld" v-else>
        <label class="fld-label">{{ $t("codAdvanceReport.customer") }}</label>
        <div class="chosen is-static">
          <span class="avatar is-lg">{{ initialOf(shop.customer) }}</span>
          <span class="person-text">
            <b class="person-name">{{ shop.customer ? fullName(shop.customer) : '-' }}</b>
            <span class="person-tel" v-if="shop.customer"><i class="fal fa-phone"></i> {{ shop.customer.tel }}</span>
          </span>
        </div>
      </div>

      <div class="status-row">
        <span class="status-text">
          <b>{{ $t("codAdvance.shop_status") }} <i class="req">*</i></b>
          <span class="status-sub">{{ form.is_enabled ? $t("codAdvance.service_on") : $t("codAdvance.service_off") }}</span>
        </span>
        <button type="button" class="toggle" :class="{'is-on': form.is_enabled}"
                role="switch" :aria-checked="String(form.is_enabled)"
                @click="form.is_enabled = !form.is_enabled">
          <span class="knob"></span>
        </button>
      </div>
      <!-- Owner, 2026-10-05: switching ON, the % withdraw and the fee go live only
           once ຜູ້ບໍລິຫານ approves; everything else here saves at once. -->
      <p class="fld-hint gated-hint" v-if="!canApprove">
        <i class="fal fa-user-shield"></i>
        {{ $t("codAdvance.approval_gated_hint") }}
      </p>

      <div class="pending-box" v-if="pendingRows.length">
        <b class="pending-title"><i class="fal fa-hourglass-half"></i> {{ $t("codAdvance.pending_manager_approval") }}</b>
        <div class="pending-row" v-for="row in pendingRows" :key="row.field">
          <span>{{ row.label }}</span>
          <span><s>{{ row.old }}</s> <i class="fal fa-long-arrow-right"></i> <b>{{ row.new }}</b></span>
        </div>
        <p class="fld-hint" v-if="!canApprove">{{ $t("codAdvance.form_includes_pending") }}</p>
        <button v-else class="cod-btn is-sm" type="button" @click="$emit('review', shop)">{{ $t("codAdvance.review_approval") }}</button>
      </div>

      <div class="stats-block" v-if="preview">
        <label class="fld-label">{{ $t("codAdvance.shop_figures") }}</label>
        <div class="stats">
          <div class="stat is-green">
            <span class="stat-top"><i class="fal fa-check-circle"></i> {{ $t("codAdvance.cod_delivered") }}</span>
            <b class="stat-val">{{ formatKip(preview.delivered.amount) }}</b>
          </div>
          <!-- The app's ຍອດ COD ຮອດປາຍທາງ (owner, 2026-10-08): live; the
               snapshot only from a backend that predates `arrived`. -->
          <div class="stat is-red">
            <span class="stat-top"><i class="fal fa-shipping-fast"></i> {{ $t("codAdvance.cod_arrived") }}</span>
            <b class="stat-val">{{ formatKip(arrivedCod) }}</b>
            <span class="stat-note" v-if="preview.in_delivery.eligibility_status !== 'ok'">{{ $t("codAdvance.data_not_ready") }}</span>
          </div>
          <div class="stat is-amber">
            <span class="stat-top"><i class="fal fa-clock"></i> {{ $t("codAdvance.avg_delivery_days") }}</span>
            <b class="stat-val">{{ $t("codAdvance.days_count", { n: preview.avg_dwell_days_30d }) }}</b>
          </div>
        </div>
        <!-- Kept from the working form: a debt an operator must see before
             re-rating a shop, and what the typed percent actually grants. -->
        <div class="stats">
          <div class="stat is-plain" v-if="preview.debt && preview.debt.outstanding">
            <span class="stat-top"><i class="fal fa-file-invoice-dollar"></i> {{ $t("codAdvance.debt_outstanding") }}</span>
            <b class="stat-val">{{ formatKip(preview.debt.outstanding) }}</b>
            <span class="stat-note" v-if="preview.debt.reserved">{{ $t("codAdvance.debt_reserved", { amount: formatKip(preview.debt.reserved) }) }}</span>
          </div>
          <div class="stat is-blue">
            <span class="stat-top"><i class="fal fa-wallet"></i> {{ $t("codAdvance.max_withdrawable") }}</span>
            <b class="stat-val">{{ formatKip(preview.max_amount_at_rate) }}</b>
          </div>
        </div>
      </div>

      <div class="two-col">
        <div class="fld">
          <label class="fld-label">{{ $t("codAdvance.set_advance_rate") }}</label>
          <div class="suffixed">
            <input class="fld-input" type="number" min="0" :max="maxRate" step="0.01"
                   v-model.number="form.advance_rate_pct" @change="loadPreview" placeholder="0">
            <span class="suffix">%</span>
          </div>
          <p class="fld-hint" v-if="policy">{{ $t("codAdvance.max_rate_hint", { pct: maxRate }) }}</p>
        </div>
        <div class="fld">
          <!-- Percent, not kip: the shop stores fee_pct and the fee is computed
               as ceil_1000(amount x pct), with the flat floor living in config. -->
          <label class="fld-label">{{ $t("codAdvance.fee") }}</label>
          <div class="suffixed">
            <input class="fld-input" type="number" min="0" :max="maxFee" step="0.01"
                   v-model.number="form.fee_pct" placeholder="0">
            <span class="suffix">%</span>
          </div>
          <p class="fld-hint" v-if="policy">
            {{ $t("codAdvance.max_fee_hint", { pct: maxFee, min: formatKip(policy.min_fee) }) }}
          </p>
        </div>
      </div>

      <div class="fld">
        <label class="fld-label">{{ $t("codAdvance.withdraw_mode") }}</label>
        <div class="mode-pills">
          <button v-for="mode in modes" :key="mode.value" type="button" class="mode-pill"
                  :class="{'is-on': form.withdraw_mode === mode.value}" @click="form.withdraw_mode = mode.value">
            <b>{{ mode.label }}</b>
            <span>{{ mode.hint }}</span>
          </button>
        </div>
      </div>

      <div class="fld">
        <label class="fld-label">{{ $t("codAdvance.max_credit_limit") }}</label>
        <div class="suffixed">
          <input class="fld-input" type="number" min="0" :max="maxCredit || undefined" v-model.number="form.credit_limit"
                 :placeholder="maxCredit ? $t('codAdvance.blank_ceiling', { amount: formatKip(maxCredit) }) : $t('codAdvance.blank_unlimited')">
          <span class="suffix">{{ $t("codAdvanceReport.kip_unit") }}</span>
        </div>
        <p class="fld-hint" v-if="maxCredit">{{ $t("codAdvance.max_credit_hint", { amount: formatKip(maxCredit) }) }}</p>
      </div>

      <div class="fld">
        <label class="fld-label">
          {{ $t("codAdvance.contract_document") }} <i class="req" v-if="!isEdit">*</i>
        </label>
        <input ref="file" class="is-hidden-input" type="file" accept=".pdf,.jpg,.jpeg,.png" @change="onFile">
        <div class="dropzone" v-if="!contract" :class="{'is-over': dragOver}"
             @click="openFilePicker" @dragover.prevent="dragOver = true"
             @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
          <i class="fal fa-cloud-upload dz-icon"></i>
          <span class="dz-text">{{ $t("codAdvance.upload_document") }}</span>
          <span class="dz-hint">{{ $t("codAdvance.upload_hint") }}</span>
        </div>
        <div class="file-picked" v-else>
          <i class="fal fa-file-alt"></i>
          <span class="picked-name">{{ contract.name }}</span>
          <span class="picked-size">{{ fileSize(contract.size) }}</span>
          <button class="chosen-x" type="button" @click="removeFile" :aria-label="$t('codAdvance.remove')">
            <i class="fal fa-times"></i>
          </button>
        </div>
        <p class="fld-hint" v-if="isEdit">{{ $t("codAdvance.blank_keep_document") }}</p>
      </div>

      <p class="form-error" v-if="error">{{ error }}</p>
    </div>

    <div class="form-foot">
      <button class="cod-btn is-block" :disabled="saving || !canSave" @click="save">
        {{ saving ? $t("codAdvance.saving") : $t("codAdvance.save_shop") }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  createShop,
  describeChanges,
  errorMessage,
  fetchCustomerPreview,
  formatKip,
  searchCodCustomers,
  updateShop,
  withdrawModes,
} from '@/services/codAdvanceService';

const MAX_CONTRACT_BYTES = 10 * 1024 * 1024;

export default {
  name: 'ShopFormModal',
  props: {
    shop: {type: Object, default: null},
    // From the list's `can_approve`: an approver's own edits apply at once.
    canApprove: {type: Boolean, default: false},
    // The boss's ceilings (list `policy`, 2026-10-26); the server refuses anything above them.
    policy: {type: Object, default: null},
  },
  data() {
    return {
      form: {
        shop_name: '',
        advance_rate_pct: 30,
        fee_pct: 3,
        credit_limit: null,
        is_enabled: true,
        withdraw_mode: 'full',
      },
      modes: withdrawModes(),
      contract: null,
      customerQuery: '',
      customerResults: [],
      selectedCustomer: null,
      preview: null,
      error: null,
      saving: false,
      searching: false,
      dragOver: false,
      searchTimer: null,
    };
  },
  computed: {
    isEdit() {
      return !!(this.shop && this.shop.id);
    },
    maxRate() {
      return this.policy ? Number(this.policy.max_rate_pct) : 100;
    },
    maxFee() {
      return this.policy ? Number(this.policy.max_fee_pct) : 100;
    },
    maxCredit() {
      return this.policy && this.policy.max_credit_limit ? Number(this.policy.max_credit_limit) : null;
    },
    arrivedCod() {
      const d = this.preview && this.preview.in_delivery;
      if (!d) return 0;
      return d.arrived !== undefined && d.arrived !== null ? d.arrived : d.base_today;
    },
    pendingRows() {
      return this.isEdit && this.shop.pending_approval
        ? describeChanges(this.shop.pending_approval.changes)
        : [];
    },
    // Mirrors save()'s guards so the button reads as disabled instead of
    // failing on click — the design shows the pale state until it is usable.
    canSave() {
      if (!this.form.shop_name) return false;
      if (!this.isEdit && !this.selectedCustomer) return false;
      if (!this.isEdit && !this.contract) return false;
      return true;
    },
  },
  created() {
    // A new shop starts at the usual 30 % / 3 %, never above the ceilings.
    if (!this.isEdit && this.policy) {
      this.form.advance_rate_pct = Math.min(this.form.advance_rate_pct, this.maxRate);
      this.form.fee_pct = Math.min(this.form.fee_pct, this.maxFee);
    }
    if (this.isEdit) {
      this.form = {
        shop_name: this.shop.shop_name,
        advance_rate_pct: this.shop.advance_rate_pct,
        fee_pct: this.shop.fee_pct,
        credit_limit: this.shop.credit_limit,
        is_enabled: this.shop.is_enabled,
        withdraw_mode: this.shop.withdraw_mode || 'full',
      };
      // A requester edits what they asked for: the form opens on the live row
      // with the waiting values over it, so saving again keeps the request
      // and putting a value back withdraws it. An approver sees the live row —
      // their save is never an approval of somebody else's request.
      const pending = this.shop.pending_approval;
      if (!this.canApprove && pending && pending.changes) {
        Object.keys(pending.changes).forEach((field) => {
          if (field in this.form) {
            this.form[field] = pending.changes[field].new;
          }
        });
      }
      this.loadPreview();
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    formatKip,
    fullName(row) {
      if (!row) return '-';
      return [row.name, row.surname].filter(Boolean).join(' ');
    },
    initialOf(row) {
      const name = this.fullName(row);
      return name && name !== '-' ? name.trim().charAt(0).toUpperCase() : '?';
    },
    isPicked(row) {
      return !!(this.selectedCustomer && this.selectedCustomer.id === row.id);
    },
    fileSize(bytes) {
      const kb = (Number(bytes) || 0) / 1024;
      return kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : Math.round(kb) + ' KB';
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      if (!this.customerQuery || this.customerQuery.length < 2) {
        this.customerResults = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this.searchTimer = setTimeout(async () => {
        try {
          this.customerResults = await searchCodCustomers(this.customerQuery);
        } catch (e) {
          this.customerResults = [];
        } finally {
          this.searching = false;
        }
      }, 350);
    },
    pickCustomer(row) {
      if (row.enrolled_shop_id) {
        this.error = this.$t('codAdvance.customer_already_enrolled');
        return;
      }
      this.error = null;
      this.selectedCustomer = row;
      if (!this.form.shop_name) {
        this.form.shop_name = this.fullName(row);
      }
      this.loadPreview();
    },
    clearCustomer() {
      this.selectedCustomer = null;
      this.preview = null;
    },
    openFilePicker() {
      this.$refs.file.click();
    },
    onFile(event) {
      this.acceptFile(event.target.files[0] || null);
    },
    onDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer && event.dataTransfer.files
        ? event.dataTransfer.files[0]
        : null;
      this.acceptFile(file);
    },
    acceptFile(file) {
      if (!file) return;
      if (!/\.(pdf|jpe?g|png)$/i.test(file.name)) {
        this.error = this.$t('codAdvance.file_type_error');
        return;
      }
      if (file.size > MAX_CONTRACT_BYTES) {
        this.error = this.$t('codAdvance.file_too_large');
        return;
      }
      this.error = null;
      this.contract = file;
    },
    removeFile() {
      this.contract = null;
      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    },
    async loadPreview() {
      const customerId = this.isEdit
        ? (this.shop.customer ? this.shop.customer.id : null)
        : (this.selectedCustomer ? this.selectedCustomer.id : null);
      if (!customerId) {
        return;
      }
      try {
        this.preview = await fetchCustomerPreview(customerId, this.form.advance_rate_pct);
      } catch (e) {
        this.preview = null;
      }
    },
    buildFormData() {
      const data = new FormData();
      data.append('shop_name', this.form.shop_name || '');
      data.append('is_enabled', this.form.is_enabled ? 1 : 0);
      data.append('advance_rate_pct', this.form.advance_rate_pct);
      data.append('fee_pct', this.form.fee_pct);
      data.append('credit_limit', this.form.credit_limit == null ? '' : this.form.credit_limit);
      data.append('withdraw_mode', this.form.withdraw_mode || 'full');
      if (this.contract) {
        data.append('contract_document', this.contract);
      }
      return data;
    },
    async save() {
      if (this.saving) return;
      this.error = null;
      if (!this.form.shop_name) {
        this.error = this.$t('codAdvance.shop_name_required');
        return;
      }
      if (!this.isEdit && !this.selectedCustomer) {
        this.error = this.$t('codAdvance.customer_required');
        return;
      }
      if (!this.isEdit && !this.contract) {
        this.error = this.$t('codAdvance.contract_required');
        return;
      }
      // The boss's ceilings — only for values this save changes: an old shop
      // above a lowered ceiling can still be renamed (the server does the same).
      const changed = (field) => !this.isEdit || Number(this.form[field]) !== Number(this.shop[field]);
      if (changed('advance_rate_pct') && Number(this.form.advance_rate_pct) > this.maxRate) {
        this.error = this.$t('codAdvance.rate_above_ceiling', {pct: this.maxRate});
        return;
      }
      if (changed('fee_pct') && Number(this.form.fee_pct) > this.maxFee) {
        this.error = this.$t('codAdvance.fee_above_ceiling', {pct: this.maxFee});
        return;
      }
      if (this.maxCredit && this.form.credit_limit !== null && this.form.credit_limit !== ''
          && changed('credit_limit') && Number(this.form.credit_limit) > this.maxCredit) {
        this.error = this.$t('codAdvance.credit_above_ceiling', {amount: formatKip(this.maxCredit)});
        return;
      }
      this.saving = true;
      try {
        const data = this.buildFormData();
        let saved;
        if (this.isEdit) {
          saved = await updateShop(this.shop.id, data);
        } else {
          data.append('customer_id', this.selectedCustomer.id);
          saved = await createShop(data);
        }
        this.$emit('saved', saved || {});
      } catch (e) {
        this.error = errorMessage(e, this.$t('codAdvance.save_failed'));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.cod-shop-form {
  text-align: left;
  display: flex;
  flex-direction: column;
  width: 640px;
  max-width: 100%;
  max-height: 72vh;
  color: $cod-ink;
}

.form-scroll {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 2px 2px 8px;
}

.picker {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
}

.pane {
  border: 1px solid $cod-line;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  height: 210px;
}

.pane-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  background: #fafbfc;
  border-bottom: 1px solid $cod-line;

  &.is-selected-head {
    background: #eef3ff;
    color: $cod-blue;
  }
}

.pane-count {
  font-weight: 500;
  color: $cod-muted;
}

.pane-body {
  flex: 1 1 auto;
  overflow-y: auto;

  &.is-centered {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.pane-empty {
  color: #b3b9c5;
  font-size: 13px;
  text-align: center;
  padding: 16px 10px;
  margin: 0;
}

.person-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f2f4f7;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: #f7f9ff;
  }

  &.is-picked {
    background: #eef3ff;
  }

  &.is-taken {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.taken-tag {
  margin-left: auto;
  font-size: 11px;
  color: #b3b9c5;
}

.picked-tick {
  margin-left: auto;
  color: $cod-blue;
}

.chosen {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f7f9ff;
  border: 1px solid #dbe3ff;
  border-radius: 12px;
  padding: 12px 14px;
  width: calc(100% - 24px);

  &.is-static {
    width: 100%;
    background: #fafbfc;
    border-color: $cod-line;
  }
}

.status-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f5f7fa;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
}

.status-text {
  display: flex;
  flex-direction: column;
  font-size: 14px;

  .req {
    color: $cod-danger;
    font-style: normal;
  }
}

.status-sub {
  font-size: 13px;
  color: $cod-muted;
}

.toggle {
  width: 52px;
  height: 30px;
  border-radius: 999px;
  border: 0;
  background: #cfd5e0;
  position: relative;
  cursor: pointer;
  transition: background 0.15s ease;
  padding: 0;

  .knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #fff;
    transition: transform 0.15s ease;
  }

  &.is-on {
    background: $cod-blue;

    .knob {
      transform: translateX(22px);
    }
  }
}

.stats-block {
  margin-bottom: 18px;
}

.gated-hint {
  margin: -10px 0 14px;

  i {
    color: $cod-blue;
    margin-right: 4px;
  }
}

.pending-box {
  background: #fff8e6;
  border: 1px solid #f5e1a8;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 18px;

  .fld-hint {
    margin: 6px 0 0;
  }

  .cod-btn {
    margin-top: 8px;
  }
}

.pending-title {
  display: block;
  color: #b07d00;
  font-size: 14px;
  margin-bottom: 6px;
}

.pending-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  padding: 3px 0;

  s {
    color: $cod-muted;
  }

  i {
    color: $cod-muted;
    margin: 0 4px;
  }
}

.mode-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.mode-pill {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
  border: 1px solid $cod-line;
  border-radius: 12px;
  background: #fff;
  padding: 10px 12px;
  cursor: pointer;
  color: $cod-ink;

  b {
    font-size: 14px;
  }

  span {
    font-size: 12px;
    color: $cod-muted;
  }

  &.is-on {
    border-color: $cod-blue;
    background: #eef3ff;

    b {
      color: $cod-blue;
    }
  }
}

.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.form-foot {
  flex: 0 0 auto;
  padding-top: 14px;
  border-top: 1px solid #f0f2f5;
  background: #fff;
}
</style>
